export const A4InvoiceTemplate = (
  cart,
  customer,
  storeInfo,
  selectedPaymentMethod,
  paidAmount,
  receiptNumber,
  discount = null,
  customVATRate = null
) => {
  console.log('📄 Generating A4 Invoice with store info:', storeInfo);
  console.log('💰 Discount:', discount);
  console.log('📊 Custom VAT Rate:', customVATRate);

  // ✅ Add debug logging to see what data we're receiving
  console.log('🏢 Store Info Fields:', storeInfo ? Object.keys(storeInfo) : 'No store info');

  const subtotal = cart.reduce((sum, item) => sum + item.Quantity * item.Price, 0);
  
  const discountAmount = discount?.amount || 0;
  const totalAfterDiscount = Math.max(0, subtotal - discountAmount);
  
  const totalVAT = cart.reduce((sum, item) => {
    const itemTotal = item.Quantity * item.Price;
    const itemRatio = subtotal > 0 ? itemTotal / subtotal : 0;
    const itemDiscountedTotal = itemTotal - (discountAmount * itemRatio);
    const vatRate = customVATRate !== null && customVATRate !== undefined ? customVATRate : item.VAT;
    return sum + ((itemDiscountedTotal * vatRate) / 100);
  }, 0);
  
  const totalIncl = totalAfterDiscount + totalVAT;
  const change = (parseFloat(paidAmount) || 0) - totalIncl;

  const vatBreakdown = {};
  cart.forEach((item) => {
    const vatRate = customVATRate !== null && customVATRate !== undefined 
      ? customVATRate 
      : parseFloat(item.VAT || 0);
    const itemTotal = item.Quantity * item.Price;
    const itemRatio = subtotal > 0 ? itemTotal / subtotal : 0;
    const itemDiscountedTotal = itemTotal - (discountAmount * itemRatio);
    const vatAmount = (itemDiscountedTotal * vatRate) / 100;

    if (!vatBreakdown[vatRate]) {
      vatBreakdown[vatRate] = { base: 0, vat: 0 };
    }
    vatBreakdown[vatRate].base += itemDiscountedTotal;
    vatBreakdown[vatRate].vat += vatAmount;
  });

  let vatTableRows = '';
  Object.keys(vatBreakdown).forEach((rate) => {
    const { base, vat } = vatBreakdown[rate];
    vatTableRows += `
      <tr>
        <td style="padding: 8px; border: 1px solid #ddd;">VAT ${rate}%</td>
        <td style="padding: 8px; border: 1px solid #ddd; text-align: right;">€${base.toFixed(2)}</td>
        <td style="padding: 8px; border: 1px solid #ddd; text-align: right;">€${vat.toFixed(2)}</td>
      </tr>
    `;
  });

  const vatIndicatorBadge = customVATRate !== null && customVATRate !== undefined && customVATRate !== 21
    ? `
      <div style="background: #e3f2fd; padding: 10px; margin: 15px 0; border-left: 4px solid #2196f3; border-radius: 4px;">
        <p style="margin: 0; color: #1976d2; font-weight: 600; font-size: 10pt;">
          ⚠️ Custom VAT Rate Applied: ${customVATRate}%
        </p>
      </div>
    `
    : '';

  // ✅ Enhanced company info extraction with better fallbacks and logging
  const companyName = storeInfo?.name || storeInfo?.Name || storeInfo?.CompanyName || 'Your Company Name';
  
  const companyAddress = storeInfo?.address || storeInfo?.Address || (storeInfo?.Street && storeInfo?.StreetNumber ? `${storeInfo.Street} ${storeInfo.StreetNumber}` : '') || '123 Street Address';
    
  const companyCity = storeInfo?.city || storeInfo?.City || 'City';
  const companyPostCode = storeInfo?.postCode || storeInfo?.PostCode || storeInfo?.PostalCode || '';
  const companyCityFull = companyPostCode 
    ? `${companyPostCode} ${companyCity}` 
    : companyCity;
    
  const companyCountry = storeInfo?.country || storeInfo?.Country || '';
  
  const companyPhone = storeInfo?.phone || storeInfo?.Phone || storeInfo?.PhoneNumber || '+32 123 456 789';
  
  const companyVAT = storeInfo?.vat || storeInfo?.VATNumber || storeInfo?.BTWNumber || storeInfo?.TaxNumber || 'BE 0123.456.789';
  
  const companyEmail = storeInfo?.email || storeInfo?.Email || '';
  const companyWebsite = storeInfo?.website || storeInfo?.Website || '';
  
  const companyIBAN = storeInfo?.iban || storeInfo?.IBAN || storeInfo?.IBANumber || '';
  const companyBIC = storeInfo?.bic || storeInfo?.BIC || storeInfo?.BICNumber || '';

  // ✅ Log extracted values for debugging
  console.log('📋 Extracted Company Data:', {
    name: companyName,
    address: companyAddress,
    city: companyCityFull,
    phone: companyPhone,
    vat: companyVAT,
    email: companyEmail,
    website: companyWebsite,
    iban: companyIBAN,
    bic: companyBIC
  });

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <style>
        @page {
          size: A4;
          margin: 15mm;
        }
        * { 
          margin: 0; 
          padding: 0; 
          box-sizing: border-box; 
        }
        body {
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          font-size: 10pt;
          line-height: 1.5;
          color: #333;
          background: white;
          padding: 0;
          margin: 0;
        }
        .invoice-container {
          width: 100%;
          max-width: 100%;
          margin: 0;
          padding: 15mm;
          background: white;
        }
        .header {
          display: flex;
          justify-content: space-between;
          margin-bottom: 25px;
          padding-bottom: 15px;
          border-bottom: 3px solid #667eea;
          gap: 20px;
        }
        .company-info {
          flex: 1;
          max-width: 50%;
        }
        .company-info h1 {
          font-size: 20pt;
          color: #667eea;
          margin-bottom: 8px;
          font-weight: 700;
        }
        .company-info p {
          margin: 2px 0;
          font-size: 9pt;
          color: #666;
          line-height: 1.4;
        }
        .invoice-details {
          text-align: right;
          flex: 1;
          max-width: 45%;
        }
        .invoice-details h2 {
          font-size: 24pt;
          color: #333;
          margin-bottom: 8px;
          font-weight: 700;
        }
        .invoice-details p {
          margin: 4px 0;
          font-size: 9pt;
        }
        .invoice-details strong {
          color: #667eea;
          font-weight: 600;
        }
        .parties {
          display: flex;
          justify-content: space-between;
          margin: 20px 0;
          gap: 20px;
        }
        .party-box {
          flex: 1;
          padding: 15px;
          background: #f8f9fa;
          border-left: 4px solid #667eea;
          border-radius: 4px;
        }
        .party-box h3 {
          font-size: 11pt;
          color: #667eea;
          margin-bottom: 8px;
          font-weight: 600;
          text-transform: uppercase;
        }
        .party-box p {
          margin: 4px 0;
          font-size: 9pt;
          color: #555;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          margin: 20px 0;
        }
        thead {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
        }
        th {
          padding: 10px 8px;
          text-align: left;
          font-weight: 600;
          font-size: 9pt;
          text-transform: uppercase;
        }
        td {
          padding: 10px 8px;
          border-bottom: 1px solid #e0e0e0;
          font-size: 9pt;
        }
        tbody tr:hover {
          background: #f8f9fa;
        }
        .text-right { text-align: right; }
        .text-center { text-align: center; }
        .totals-section {
          display: flex;
          justify-content: flex-end;
          margin-top: 25px;
        }
        .totals-box {
          width: 350px;
          max-width: 100%;
          border: 2px solid #667eea;
          border-radius: 8px;
          overflow: hidden;
        }
        .totals-box table {
          margin: 0;
        }
        .totals-box td {
          padding: 8px 12px;
          border: none;
          border-bottom: 1px solid #e0e0e0;
          font-size: 9pt;
        }
        .totals-box tr:last-child td {
          border-bottom: none;
        }
        .discount-row {
          background: #ffebee !important;
          color: #d32f2f !important;
          font-weight: 700 !important;
        }
        .discount-row td {
          color: #d32f2f !important;
          font-weight: 700 !important;
        }
        .after-discount-row {
          background: #e8f5e9 !important;
        }
        .after-discount-row td {
          color: #2e7d32 !important;
          font-weight: 600 !important;
        }
        .grand-total {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white !important;
          font-size: 13pt !important;
          font-weight: 700 !important;
        }
        .grand-total td {
          color: white !important;
          padding: 12px !important;
        }
        .savings-badge {
          background: #4caf50;
          color: white;
          padding: 10px 15px;
          border-radius: 8px;
          text-align: center;
          margin-top: 12px;
          font-weight: 700;
          font-size: 10pt;
        }
        .payment-info {
          margin-top: 25px;
          padding: 15px;
          background: #f8f9fa;
          border-radius: 8px;
          border-left: 4px solid #667eea;
        }
        .payment-info h3 {
          color: #667eea;
          margin-bottom: 12px;
          font-size: 11pt;
        }
        .payment-info p {
          margin: 6px 0;
          font-size: 9pt;
        }
        .bank-details {
          margin-top: 25px;
          padding: 15px;
          background: #f0f4ff;
          border-radius: 8px;
          border: 1px solid #c5d3ff;
        }
        .bank-details h3 {
          color: #667eea;
          margin-bottom: 12px;
          font-size: 11pt;
        }
        .bank-details p {
          margin: 6px 0;
          font-size: 9pt;
          font-family: 'Courier New', monospace;
        }
        .footer {
          margin-top: 30px;
          padding-top: 15px;
          border-top: 2px solid #e0e0e0;
          text-align: center;
          font-size: 8pt;
          color: #999;
        }
        .footer p {
          margin: 4px 0;
        }
        @media print {
          body { 
            padding: 0; 
            margin: 0;
          }
          .invoice-container { 
            width: 100%;
            max-width: 100%;
            padding: 10mm;
          }
        }
      </style>
    </head>
    <body>
      <div class="invoice-container">
        <!-- Header -->
        <div class="header">
          <div class="company-info">
            <h1>${companyName}</h1>
            <p><strong>Address:</strong> ${companyAddress}</p>
            <p>${companyCityFull}${companyCountry ? ', ' + companyCountry : ''}</p>
            <p><strong>Phone:</strong> ${companyPhone}</p>
            <p><strong>VAT Number:</strong> ${companyVAT}</p>
            ${companyEmail ? `<p><strong>Email:</strong> ${companyEmail}</p>` : ''}
            ${companyWebsite ? `<p><strong>Website:</strong> ${companyWebsite}</p>` : ''}
          </div>
          <div class="invoice-details">
            <h2>Ticket</h2>
            <p><strong>Ticket #:</strong> ${receiptNumber}</p>
            <p><strong>Date:</strong> ${new Date().toLocaleDateString('en-GB')}</p>
            <p><strong>Time:</strong> ${new Date().toLocaleTimeString('en-GB')}</p>
          </div>
        </div>

        ${vatIndicatorBadge}

        <!-- Parties -->
        <div class="parties">
          <div class="party-box">
            <h3>From</h3>
            <p><strong>${companyName}</strong></p>
            <p>${companyAddress}</p>
            <p>${companyCityFull}${companyCountry ? ', ' + companyCountry : ''}</p>
            <p>VAT: ${companyVAT}</p>
            <p>Phone: ${companyPhone}</p>
          </div>
          <div class="party-box">
            <h3>Bill To</h3>
            ${customer ? `
              <p><strong>${customer.CompanyName || customer.Name || 'Walk-in Customer'}</strong></p>
              ${customer.Address ? `<p>${customer.Address}</p>` : ''}
              ${customer.Phone ? `<p>Phone: ${customer.Phone}</p>` : ''}
              ${customer.Email ? `<p>Email: ${customer.Email}</p>` : ''}
              ${customer.VATNumber ? `<p>VAT: ${customer.VATNumber}</p>` : ''}
            ` : `
              <p><strong>Walk-in Customer</strong></p>
              <p>No customer details provided</p>
            `}
          </div>
        </div>

        <!-- Items Table -->
        <table>
          <thead>
            <tr>
              <th style="width: 45%;">Description</th>
              <th class="text-center" style="width: 12%;">Quantity</th>
              <th class="text-right" style="width: 15%;">Unit Price</th>
              <th class="text-center" style="width: 10%;">VAT %</th>
              <th class="text-right" style="width: 18%;">Total</th>
            </tr>
          </thead>
          <tbody>
            ${cart.map(item => {
              const displayVAT = customVATRate !== null && customVATRate !== undefined ? customVATRate : item.VAT;
              return `
              <tr>
                <td>${item.Name || 'N/A'}</td>
                <td class="text-center">${item.Quantity}</td>
                <td class="text-right">€${item.Price.toFixed(2)}</td>
                <td class="text-center">${displayVAT}%</td>
                <td class="text-right">€${(item.Quantity * item.Price).toFixed(2)}</td>
              </tr>
              `;
            }).join('')}
          </tbody>
        </table>

        <!-- Totals -->
        <div class="totals-section">
          <div class="totals-box">
            <table>
              <tr>
                <td>Subtotal (Excl. VAT)</td>
                <td class="text-right">€${subtotal.toFixed(2)}</td>
              </tr>
              ${discount ? `
              <tr class="discount-row">
                <td>
                  💰 Discount ${discount.type === 'percentage' ? `(${discount.value}%)` : ''}
                  ${discount.reason ? `<br><span style="font-size: 8pt; font-weight: normal;">${discount.reason}</span>` : ''}
                </td>
                <td class="text-right">-€${discountAmount.toFixed(2)}</td>
              </tr>
              <tr class="after-discount-row">
                <td>After Discount</td>
                <td class="text-right">€${totalAfterDiscount.toFixed(2)}</td>
              </tr>
              ` : ''}
              ${vatTableRows}
              <tr>
                <td><strong>Total VAT</strong></td>
                <td class="text-right"><strong>€${totalVAT.toFixed(2)}</strong></td>
              </tr>
              <tr class="grand-total">
                <td>TOTAL (Incl. VAT)</td>
                <td class="text-right">€${totalIncl.toFixed(2)}</td>
              </tr>
            </table>
            ${discount ? `
              <div class="savings-badge">
                🎉 You saved €${discountAmount.toFixed(2)} with this discount!
              </div>
            ` : ''}
          </div>
        </div>

        <!-- Payment Info -->
        <div class="payment-info">
          <h3>Payment Information</h3>
          <p><strong>Payment Method:</strong> ${selectedPaymentMethod}</p>
          ${paidAmount ? `
            <p><strong>Amount Paid:</strong> €${parseFloat(paidAmount).toFixed(2)}</p>
            <p><strong>Change:</strong> €${change.toFixed(2)}</p>
          ` : ''}
          <p><strong>Payment Status:</strong> ${paidAmount && parseFloat(paidAmount) >= totalIncl ? 'PAID' : 'PENDING'}</p>
        </div>

        <!-- Bank Details -->
        ${companyIBAN || companyBIC ? `
          <div class="bank-details">
            <h3>Bank Details</h3>
            ${companyIBAN ? `<p><strong>IBAN:</strong> ${companyIBAN}</p>` : ''}
            ${companyBIC ? `<p><strong>BIC/SWIFT:</strong> ${companyBIC}</p>` : ''}
            <p><strong>Beneficiary:</strong> ${companyName}</p>
          </div>
        ` : ''}

        <!-- Footer -->
        <div class="footer">
          <p><strong>Thank you for your business!</strong></p>
          ${companyWebsite ? `<p>${companyWebsite}</p>` : ''}
          <p>This is a computer-generated invoice and does not require a signature.</p>
        </div>
      </div>
    </body>
    </html>
  `;
};